import { useEffect } from 'react'
import { Navigate, NavLink, Route, Routes, useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from './auth/AuthContext.tsx'
import { useSettings, useUsers } from './api/hooks.ts'
import { syncSubscription } from './push/usePush.ts'
import { Login } from './screens/Login.tsx'
import { HistoryScreen } from './screens/HistoryScreen.tsx'
import { AuditScreen } from './screens/AuditScreen.tsx'
import { ChildHome } from './screens/ChildHome.tsx'
import { AdminHome } from './screens/AdminHome.tsx'
import { LedgerScreen } from './screens/LedgerScreen.tsx'
import { SettingsScreen } from './screens/SettingsScreen.tsx'
import { TaskEditor, TasksScreen } from './screens/Tasks.tsx'
import { Spinner } from './components/ui.tsx'
import { OfflineBanner } from './components/OfflineBanner.tsx'

export function App() {
  const { user, loading } = useAuth()

  if (loading) return <Spinner label="Abriendo…" />
  if (!user) return <Login />

  return (
    // Altura fija de pantalla con la lista desplazándose DENTRO, en vez de la
    // página entera desplazándose bajo una barra `fixed`: en iOS, un elemento
    // fijo se descoloca mientras la barra de direcciones se encoge al hacer
    // scroll, y la barra de navegación acababa flotando a media pantalla.
    // Siendo parte de la maquetación, no hay nada que se pueda descolocar.
    //
    // `dvh` y no `vh`: `vh` mide la pantalla con la barra del navegador
    // desplegada, así que dejaría la barra de abajo fuera de la vista.
    <div
      className="flex h-[100dvh] flex-col"
      style={{
        paddingTop: 'env(safe-area-inset-top)',
        paddingLeft: 'env(safe-area-inset-left)',
        paddingRight: 'env(safe-area-inset-right)',
      }}
    >
      <OfflineBanner />
      <PushBridge />
      {/* El hueco de abajo también aquí: en las pantallas que ocultan la barra
          (desglose, editor) no hay quien lo ponga, y el último renglón quedaría
          debajo de la raya del iPhone. */}
      <main
        className="min-h-0 flex-1 overflow-y-auto overscroll-contain"
        style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
      >
        <Routes>
          <Route path="/" element={<Navigate to={user.role === 'ADMIN' ? '/admin' : '/hijo'} replace />} />
          <Route path="/hijo" element={<ChildHome />} />
          <Route path="/familia/:userId" element={<ChildHome />} />
          <Route path="/admin" element={user.role === 'ADMIN' ? <AdminHome /> : <Navigate to="/hijo" replace />} />
          <Route path="/tareas" element={user.role === 'ADMIN' ? <TasksScreen /> : <Navigate to="/" replace />} />
          <Route path="/tareas/:id" element={user.role === 'ADMIN' ? <TaskEditor /> : <Navigate to="/" replace />} />
          <Route path="/semana/:weekId/desglose" element={<LedgerScreen />} />
          <Route path="/historial" element={<HistoryScreen />} />
          <Route path="/historial/:userId" element={<HistoryScreen />} />
          <Route
            path="/registro"
            element={user.role === 'ADMIN' ? <AuditScreen /> : <Navigate to="/" replace />}
          />
          <Route path="/ajustes" element={<SettingsScreen />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>
      <BottomNav />
    </div>
  )
}

/**
 * Puente entre el service worker y la navegación de la app.
 *
 * Hace dos cosas al entrar:
 *  - Reenvía la suscripción push al servidor. Se pierde al reinstalar la PWA, y
 *    esto la autorrepara sin que nadie tenga que volver a Ajustes.
 *  - Atiende el mensaje del service worker cuando se toca una notificación con
 *    la app ya abierta, para navegar dentro en vez de abrir otra ventana.
 */
function PushBridge() {
  const navigate = useNavigate()

  useEffect(() => {
    void syncSubscription()

    const onMessage = (event: MessageEvent) => {
      if (event.data?.type === 'navigate' && typeof event.data.url === 'string') {
        try {
          const url = new URL(event.data.url)
          if (url.origin === window.location.origin) navigate(url.pathname + url.search)
        } catch {
          /* una URL inválida no debe romper nada */
        }
      }
    }

    navigator.serviceWorker?.addEventListener('message', onMessage)
    return () => navigator.serviceWorker?.removeEventListener('message', onMessage)
  }, [navigate])

  return null
}

function BottomNav() {
  const { user } = useAuth()
  const users = useUsers()
  const settings = useSettings()
  const location = useLocation()

  if (!user) return null
  // En pantallas de detalle estorba mas que ayuda.
  if (location.pathname.includes('/desglose') || location.pathname.startsWith('/tareas/')) return null

  const sibling =
    user.role === 'CHILD' && settings.data?.siblingsCanViewEachOther
      ? (users.data ?? []).find((u) => u.role === 'CHILD' && u.id !== user.id)
      : undefined

  const items =
    user.role === 'ADMIN'
      ? [
          { to: '/admin', label: 'Inicio', icon: '◉' },
          { to: '/tareas', label: 'Tareas', icon: '☰' },
          { to: '/registro', label: 'Registro', icon: '🕘' },
          { to: '/ajustes', label: 'Ajustes', icon: '⚙' },
        ]
      : [
          { to: '/hijo', label: 'Inicio', icon: '◉' },
          { to: '/historial', label: 'Historial', icon: '📈' },
          ...(sibling ? [{ to: `/familia/${sibling.id}`, label: sibling.name, icon: '👤' }] : []),
          { to: '/ajustes', label: 'Ajustes', icon: '⚙' },
        ]

  return (
    // Sin `fixed`: es el último hijo de una columna de altura fija, así que se
    // queda abajo por maquetación. El hueco de la barra inferior del iPhone lo
    // pone ella, que es quien lo toca.
    <nav
      className="shrink-0 border-t border-[var(--color-line)] bg-[var(--color-surface)]"
      style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
    >
      <div className="mx-auto flex max-w-lg">
        {items.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            className={({ isActive }) =>
              `flex flex-1 flex-col items-center gap-0.5 py-2.5 text-[11px] ${
                isActive ? 'text-[var(--color-brand)]' : 'text-[var(--color-muted)]'
              }`
            }
          >
            <span className="text-lg leading-none">{item.icon}</span>
            {item.label}
          </NavLink>
        ))}
      </div>
    </nav>
  )
}
